import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { hideSlideoutMenu } from '@mm/state/actions/viewActions';
import { Contact, SelectedContact } from '@mm/state/interfaces/contacts';
import {
  connectedPageSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useDetectOutsideClicks } from '../../../../../../common/hooks/use-detect-outside-clicks';
import { SlideoutMenu } from '../../../../../components/SlideoutMenu/components/SlideoutMenu';
import ContactsDetails from '../../../../contacts/components/contactDetails/contactDetails';
import './ContactSlideoutMenu.pcss';

export type ContactSlideoutMenuProps = {
  className?: string;
  contactId?: number;
  selectedContact?: SelectedContact;
  deleteSelectedContact?: (
    contactId: number,
    eraseRemoteMessages: boolean,
  ) => Promise<void>;
  onContactSelect?: (contactId: number) => void;
  onContactUpdateSuccess?: () => void;
};

export const ContactSlideoutMenu = ({
  className,
  contactId,
  selectedContact,
  onContactSelect = () => {},
  onContactUpdateSuccess = () => {},
  deleteSelectedContact = async () => {},
}: ContactSlideoutMenuProps) => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const connectedPage = useSelector(connectedPageSelector);
  const activeCompanyId = useSelector(selectActiveCompanyId);

  const [contact, setContact] = useState<SelectedContact | undefined>(
    selectedContact?.dummy ? selectedContact : undefined,
  );
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);

  const contactsCRUDActions = useContactsCRUDActions();
  const fetchContact = useCallback(() => {
    (async () => {
      if (
        contactId &&
        connectedPage &&
        activeCompanyId &&
        !selectedContact?.dummy
      ) {
        setBusy(true);
        const contact = await contactsCRUDActions.retrieveContactById(
          activeCompanyId,
          contactId,
          connectedPage.id,
          true,
        );
        setBusy(false);
        if (contact) {
          setContact(contact);
        }
      }
    })();
  }, [contactId, connectedPage, activeCompanyId, contactsCRUDActions]);

  useEffect(fetchContact, [contactId]);

  const onContactUpdate = () => {
    fetchContact();
    onContactUpdateSuccess();
  };

  const onContactsClose = () => dispatch(hideSlideoutMenu());

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };

  useDetectOutsideClicks(() => {
    onContactsClose();
  }, wrapperRef);

  if (!contactId) return null;

  return (
    <SlideoutMenu
      className={classNames(className, 'contact-slideout-menu')}
      setRef={setRef}
    >
      <SlideoutMenu.Content
        className="contact-slideout-menu__content"
        busy={busy}
      >
        {contact && (
          <ContactsDetails
            closeDetails={onContactsClose}
            selectedContact={contact}
            onDeleteSelectedContact={deleteSelectedContact}
            onContactSelect={onContactSelect}
            refreshSelectedContact={onContactUpdate}
          />
        )}
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
